import React, { useState, useEffect } from 'react'
import Sidebar from '../mini-comp/Sidebar'
import Topbar from '../mini-comp/Topbar'
import axios from 'axios';
import Cookies from 'js-cookie';
import API_BASE_URL from '../../../config/api';
import Swal from 'sweetalert2';
import { RiEyeLine, RiEditLine, RiDeleteBinLine, RiBuildingLine, RiCameraLine } from 'react-icons/ri';

const AllCompanies = () => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [companyLogoPreview, setCompanyLogoPreview] = useState(null);
  const [editingCompany, setEditingCompany] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [formData, setFormData] = useState({
    logo: null,
    trade_name: '',
    currency: '',
    phone_number: '',
    email: '',
    address: '',
    city: '',
    employee_id_prefix: '',
    md_email: '',
    gratuity_increment_type: '',
    gratuity_start_years: ''
  });

  const fetchCompanies = () => {
    const token = Cookies.get('token');
    if (!token) {
      console.error('No token found in cookies');
      setLoading(false);
      return;
    }

    axios.get(`${API_BASE_URL}/api/v1/billing/companies/`, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    })
      .then(response => {
        console.log('Companies response:', response.data);
        // Handle different response structures
        const companiesData = response.data.companies || response.data || [];
        setCompanies(Array.isArray(companiesData) ? companiesData : []);
        setLoading(false);
      })
      .catch(error => {
        console.error('Error fetching companies:', error);
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchCompanies();
  }, []);

  const handleInputChange = (e) => {
    const { id, value, name } = e.target;
    const fieldName = name || id;
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  const handleFileChange = (e, field) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({
        ...prev,
        [field]: file
      }));

      // Preview image
      const reader = new FileReader();
      reader.onloadend = () => {
        if (field === 'logo') {
          setCompanyLogoPreview(reader.result);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleEdit = (company) => {
    setEditingCompany(company);
    setIsEditMode(true);
    setFormData({
      logo: null,
      trade_name: company.trade_name || '',
      currency: company.currency || '',
      phone_number: company.phone_number || '',
      email: company.email || '',
      address: company.address || '',
      city: company.city || '',
      employee_id_prefix: company.employee_id_prefix || '',
      md_email: company.md_email || '',
      gratuity_increment_type: company.gratuity_increment_type || '',
      gratuity_start_years: company.gratuity_start_years || ''
    });
    // Set logo preview if company has a logo
    if (company.logo) {
      setCompanyLogoPreview(company.logo);
    }
  };

  const handleCloseModal = () => {
    setIsEditMode(false);
    setEditingCompany(null);
    setFormData({
      logo: null,
      trade_name: '',
      currency: '',
      phone_number: '',
      email: '',
      address: '',
      city: '',
      employee_id_prefix: '',
      md_email: '',
      gratuity_increment_type: '',
      gratuity_start_years: ''
    });
    setCompanyLogoPreview(null);
  };

  const handleDelete = async (company) => {
    // Show confirmation dialog
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: `Do you want to delete "${company.trade_name || company.company_name}"? This action cannot be undone!`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    });

    // If user confirmed, proceed with deletion
    if (result.isConfirmed) {
      const token = Cookies.get('token');
      if (!token) {
        console.error('No token found in cookies');
        return;
      }

      try {
        await axios.delete(`${API_BASE_URL}/api/v1/billing/companies/${company.id}/`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        // Show success message
        Swal.fire({
          icon: 'success',
          title: 'Deleted!',
          text: 'Company has been deleted successfully.',
          confirmButtonText: 'OK'
        });

        // Refresh companies list
        fetchCompanies();
      } catch (error) {
        console.error('Error deleting company:', error);

        // Show error message
        Swal.fire({
          icon: 'error',
          title: 'Error!',
          text: error.response?.data?.message || 'Failed to delete company. Please try again.',
          confirmButtonText: 'OK'
        });
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    const token = Cookies.get('token');
    if (!token) {
      console.error('No token found in cookies');
      setSubmitting(false);
      return;
    }

    // Create FormData for file uploads
    const submitData = new FormData();
    submitData.append('trade_name', formData.trade_name);
    submitData.append('currency', formData.currency);
    submitData.append('phone_number', formData.phone_number);
    submitData.append('email', formData.email);
    submitData.append('address', formData.address);
    submitData.append('city', formData.city);
    submitData.append('employee_id_prefix', formData.employee_id_prefix);
    submitData.append('md_email', formData.md_email);
    submitData.append('gratuity_increment_type', formData.gratuity_increment_type);

    if (formData.gratuity_increment_type === 'incremental' && formData.gratuity_start_years) {
      submitData.append('gratuity_start_years', formData.gratuity_start_years);
    }

    if (formData.logo) {
      submitData.append('logo', formData.logo);
    }

    try {
      let response;
      if (isEditMode && editingCompany) {
        // Update existing company
        response = await axios.put(`${API_BASE_URL}/api/v1/billing/companies/${editingCompany.id}/`, submitData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${token}`
          }
        });
        console.log('Company updated successfully:', response.data);

        // Show success alert for update
        Swal.fire({
          icon: 'success',
          title: 'Success!',
          text: 'Company updated successfully',
          confirmButtonText: 'OK'
        });
      } else {
        // Create new company
        response = await axios.post(`${API_BASE_URL}/api/v1/billing/companies/`, submitData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${token}`
          }
        });
        console.log('Company created successfully:', response.data);

        // Show success alert for creation
        Swal.fire({
          icon: 'success',
          title: 'Success!',
          text: 'Company created successfully',
          confirmButtonText: 'OK'
        });
      }

      // Close modal
      const modalId = isEditMode ? 'editCompany' : 'addNewCompany';
      const modal = document.getElementById(modalId);
      if (modal) {
        const bsModal = window.bootstrap?.Modal?.getInstance(modal);
        if (bsModal) {
          bsModal.hide();
        } else {
          // Fallback if Bootstrap modal instance not available
          modal.classList.remove('show');
          modal.style.display = 'none';
          document.body.classList.remove('modal-open');
          const backdrop = document.querySelector('.modal-backdrop');
          if (backdrop) backdrop.remove();
        }
      }

      // Reset form
      handleCloseModal();

      // Refresh companies list
      fetchCompanies();

      setSubmitting(false);
    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} company:`, error);

      // Show error alert
      Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: error.response?.data?.message || `Failed to ${isEditMode ? 'update' : 'create'} company. Please try again.`,
        confirmButtonText: 'OK'
      });

      setSubmitting(false);
    }
  };

  return (
    <div>
      <Topbar />
      <Sidebar />

      {/* app-content-area-start */}
      <div className="app-content-area">
        <div className="app-content-wrap">
          <div className="container-fluid">
            <div className="row">
              <div className="col-xl-12">
                <div className="card">
                  <div className="card-header justify-between">
                    <h4 className="d-flex-items gap-10">All Companies<span className="badge bg-label-primary">{companies.length}</span></h4>
                    <div className="d-flex flex-wrap gap-15">
                      <button type="button" className="btn btn-primary" data-bs-toggle="modal" data-bs-target="#addNewCompany">
                        Add Company
                      </button>
                      <a className="btn btn-success text-white" href="javascript:void(0);">Export As
                        Excel
                      </a>

                    </div>
                  </div>
                  <div className="card-body pt-15">
                    <div className="table-responsive">
                      <table className="table text-nowrap w-100" id="dataTableDefault">
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Location</th>
                            <th>Phone</th>
                            <th>Email</th>
                            <th>Status</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {loading ? (
                            <tr>
                              <td colSpan="6" className="text-center py-4">
                                <span className="text-muted">Loading...</span>
                              </td>
                            </tr>
                          ) : companies && companies.length > 0 ? (
                            companies.map((company) => (
                              <tr key={company.id}>
                                <td>{company.trade_name || company.company_name || 'Unnamed Company'}</td>
                                <td>{company.city || 'N/A'}</td>
                                <td>{company.phone_number || 'N/A'}</td>
                                <td>{company.email || 'N/A'}</td>
                                <td>
                                  {company.has_subscription === true ? (
                                    <span className="badge bg-label-success">Active</span>
                                  ) : (
                                    <span className="badge bg-label-danger">Inactive</span>
                                  )}
                                </td>
                                <td>
                                  <div className="d-flex-items gap-5">
                                    <a className="btn-icon btn-success-light" href={`/admin/company-details/${company.id}`}>
                                      <RiEyeLine />
                                    </a>
                                    <button className="btn-icon btn-info-light" type="button" data-bs-toggle="modal" data-bs-target="#editCompany" onClick={() => handleEdit(company)}>
                                      <RiEditLine />
                                    </button>
                                    <button className="btn-icon btn-danger-light" type="button" onClick={() => handleDelete(company)}>
                                      <RiDeleteBinLine />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="6" className="text-center py-4">
                                <span className="text-muted">No companies found</span>
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <>{ /* create companies model start */}
            <div className="modal fade" id="addNewCompany" tabIndex={-1} aria-labelledby="addNewCompanyLabel" aria-hidden="true">
              <div className="modal-dialog modal-dialog-centered modal-lg">
                <div className="modal-content">
                  <form onSubmit={handleSubmit}>
                    <div className="modal-header">
                      <h1 className="modal-title fs-16" id="addNewCompanyLabel">Add New Company</h1>
                      <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" />
                    </div>
                    <div className="modal-body">
                      <div className="row gy-15">
                        <div className="col-xl-12">
                          <div className="text-center">
                            <div className="avatar avatar-xxl radius-100">
                              {companyLogoPreview ? (
                                <img src={companyLogoPreview} alt="Company Logo" className="radius-100" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                              ) : (
                                <div className="d-flex align-items-center justify-content-center w-100 h-100" style={{ backgroundColor: '#f0f0f0', borderRadius: '50%' }}>
                                  <RiBuildingLine style={{ fontSize: '3rem', color: '#6c757d' }} />
                                </div>
                              )}
                              <span className="badge rounded-pill bg-primary avatar-badge">
                                <input
                                  type="file"
                                  name="photo"
                                  className="p-absolute z-3 cursor-pointer w-100 h-100 op-0 pl-0 pr-0"
                                  id="profileImageChange"
                                  accept="image/*"
                                  onChange={(e) => handleFileChange(e, 'logo')}
                                />
                                <RiCameraLine />
                              </span>
                            </div>
                            <span className="d-block fw-5 text-muted">Company Logo</span>
                          </div>
                        </div>

                        <div className="col-xl-12">
                          <label htmlFor="trade_name" className="form-label">Trade Name <span className="text-danger">*</span></label>
                          <input
                            type="text"
                            className="form-control"
                            id="trade_name"
                            name="trade_name"
                            placeholder="Trade Name"
                            value={formData.trade_name}
                            onChange={handleInputChange}
                            required
                          />
                        </div>

                        <div className="col-xl-12">
                          <label htmlFor="currency" className="form-label">Currency <span className="text-danger">*</span></label>
                          <select
                            className="form-select"
                            id="currency"
                            name="currency"
                            value={formData.currency}
                            onChange={handleInputChange}
                            required
                          >
                            <option value="">Select Currency</option>
                            <option value="USD">USD</option>
                            <option value="ZIG">ZIG</option>
                          </select>
                        </div>

                        <div className="col-xl-6">
                          <label htmlFor="phone_number" className="form-label">Phone Number <span className="text-danger">*</span></label>
                          <input
                            type="text"
                            className="form-control"
                            id="phone_number"
                            name="phone_number"
                            placeholder="Phone Number"
                            value={formData.phone_number}
                            onChange={handleInputChange}
                            required
                          />
                        </div>
                        <div className="col-xl-6">
                          <label htmlFor="email" className="form-label">Email <span className="text-danger">*</span></label>
                          <input
                            type="email"
                            className="form-control"
                            id="email"
                            name="email"
                            placeholder="Email"
                            value={formData.email}
                            onChange={handleInputChange}
                            required
                          />
                        </div>
                        <div className="col-xl-12">
                          <label htmlFor="address" className="form-label">Address</label>
                          <input
                            type="text"
                            className="form-control"
                            id="address"
                            name="address"
                            placeholder="Address"
                            value={formData.address}
                            onChange={handleInputChange}
                          />
                        </div>
                        <div className="col-xl-6">
                          <label htmlFor="city" className="form-label">City</label>
                          <input
                            type="text"
                            className="form-control"
                            id="city"
                            name="city"
                            placeholder="City"
                            value={formData.city}
                            onChange={handleInputChange}
                          />
                        </div>
                        <div className="col-xl-6">
                          <label htmlFor="employee_id_prefix" className="form-label">Employee ID Prefix</label>
                          <input
                            type="text"
                            className="form-control"
                            id="employee_id_prefix"
                            name="employee_id_prefix"
                            placeholder="Employee ID Prefix"
                            value={formData.employee_id_prefix}
                            onChange={handleInputChange}
                          />
                        </div>
                        <div className="col-xl-6">
                          <label htmlFor="md_email" className="form-label">MD Email</label>
                          <input
                            type="email"
                            className="form-control"
                            id="md_email"
                            name="md_email"
                            placeholder="MD Email"
                            value={formData.md_email}
                            onChange={handleInputChange}
                          />
                        </div>
                        <div className="col-xl-6">
                          <label htmlFor="gratuity_increment_type" className="form-label">Gratuity Increment Type</label>
                          <select
                            className="form-select"
                            id="gratuity_increment_type"
                            name="gratuity_increment_type"
                            value={formData.gratuity_increment_type}
                            onChange={handleInputChange}
                          >
                            <option value="">Select gratuity increment type</option>
                            <option value="fixed">Fixed Rate</option>
                            <option value="incremental">Incremental Rate</option>
                          </select>
                        </div>
                        {formData.gratuity_increment_type === 'incremental' && (
                          <div className="col-xl-6">
                            <label htmlFor="gratuity_start_years" className="form-label">Gratuity Start Years</label>
                            <select
                              className="form-select"
                              id="gratuity_start_years"
                              name="gratuity_start_years"
                              value={formData.gratuity_start_years}
                              onChange={handleInputChange}
                            >
                              <option value="">Select gratuity start years</option>
                              <option value="3">3 Years</option>
                              <option value="4">4 Years</option>
                              <option value="5">5 Years</option>
                            </select>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="modal-footer">
                      <button type="button" className="btn btn-danger" data-bs-dismiss="modal" disabled={submitting}>Cancel</button>
                      <button type="submit" className="btn btn-primary" disabled={submitting}>
                        {submitting ? 'Saving...' : 'Save changes'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
            { /* create companies model end */}

            { /* edit company modal start */}
            <div className="modal fade" id="editCompany" tabIndex={-1} aria-labelledby="editCompanyLabel" aria-hidden="true">
              <div className="modal-dialog modal-dialog-centered modal-lg">
                <div className="modal-content">
                  <form onSubmit={handleSubmit}>
                    <div className="modal-header">
                      <h1 className="modal-title fs-16" id="editCompanyLabel">Edit Company</h1>
                      <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" onClick={handleCloseModal} />
                    </div>
                    <div className="modal-body">
                      <div className="row gy-15">
                        <div className="col-xl-12">
                          <div className="text-center">
                            <div className="avatar avatar-xxl radius-100">
                              {companyLogoPreview ? (
                                <img src={companyLogoPreview} alt="Company Logo" className="radius-100" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                              ) : (
                                <div className="d-flex align-items-center justify-content-center w-100 h-100" style={{ backgroundColor: '#f0f0f0', borderRadius: '50%' }}>
                                  <RiBuildingLine style={{ fontSize: '3rem', color: '#6c757d' }} />
                                </div>
                              )}
                              <span className="badge rounded-pill bg-primary avatar-badge">
                                <input
                                  type="file"
                                  name="photo"
                                  className="p-absolute z-3 cursor-pointer w-100 h-100 op-0 pl-0 pr-0"
                                  id="editProfileImageChange"
                                  accept="image/*"
                                  onChange={(e) => handleFileChange(e, 'logo')}
                                />
                                <RiCameraLine />
                              </span>
                            </div>
                            <span className="d-block fw-5 text-muted">Company Logo</span>
                          </div>
                        </div>

                        <div className="col-xl-12">
                          <label htmlFor="edit_trade_name" className="form-label">Trade Name <span className="text-danger">*</span></label>
                          <input
                            type="text"
                            className="form-control"
                            id="edit_trade_name"
                            name="trade_name"
                            placeholder="Trade Name"
                            value={formData.trade_name}
                            onChange={handleInputChange}
                            required
                          />
                        </div>

                        <div className="col-xl-12">
                          <label htmlFor="edit_currency" className="form-label">Currency <span className="text-danger">*</span></label>
                          <select
                            className="form-select"
                            id="edit_currency"
                            name="currency"
                            value={formData.currency}
                            onChange={handleInputChange}
                            required
                          >
                            <option value="">Select Currency</option>
                            <option value="USD">USD</option>
                            <option value="ZIG">ZIG</option>
                          </select>
                        </div>

                        <div className="col-xl-6">
                          <label htmlFor="edit_phone_number" className="form-label">Phone Number <span className="text-danger">*</span></label>
                          <input
                            type="text"
                            className="form-control"
                            id="edit_phone_number"
                            name="phone_number"
                            placeholder="Phone Number"
                            value={formData.phone_number}
                            onChange={handleInputChange}
                            required
                          />
                        </div>
                        <div className="col-xl-6">
                          <label htmlFor="edit_email" className="form-label">Email <span className="text-danger">*</span></label>
                          <input
                            type="email"
                            className="form-control"
                            id="edit_email"
                            name="email"
                            placeholder="Email"
                            value={formData.email}
                            onChange={handleInputChange}
                            required
                          />
                        </div>
                        <div className="col-xl-12">
                          <label htmlFor="edit_address" className="form-label">Address</label>
                          <input
                            type="text"
                            className="form-control"
                            id="edit_address"
                            name="address"
                            placeholder="Address"
                            value={formData.address}
                            onChange={handleInputChange}
                          />
                        </div>
                        <div className="col-xl-6">
                          <label htmlFor="edit_city" className="form-label">City</label>
                          <input
                            type="text"
                            className="form-control"
                            id="edit_city"
                            name="city"
                            placeholder="City"
                            value={formData.city}
                            onChange={handleInputChange}
                          />
                        </div>
                        <div className="col-xl-6">
                          <label htmlFor="edit_employee_id_prefix" className="form-label">Employee ID Prefix</label>
                          <input
                            type="text"
                            className="form-control"
                            id="edit_employee_id_prefix"
                            name="employee_id_prefix"
                            placeholder="Employee ID Prefix"
                            value={formData.employee_id_prefix}
                            onChange={handleInputChange}
                          />
                        </div>
                        <div className="col-xl-6">
                          <label htmlFor="edit_md_email" className="form-label">MD Email</label>
                          <input
                            type="email"
                            className="form-control"
                            id="edit_md_email"
                            name="md_email"
                            placeholder="MD Email"
                            value={formData.md_email}
                            onChange={handleInputChange}
                          />
                        </div>
                        <div className="col-xl-6">
                          <label htmlFor="edit_gratuity_increment_type" className="form-label">Gratuity Increment Type</label>
                          <select
                            className="form-select"
                            id="edit_gratuity_increment_type"
                            name="gratuity_increment_type"
                            value={formData.gratuity_increment_type}
                            onChange={handleInputChange}
                          >
                            <option value="">Select gratuity increment type</option>
                            <option value="fixed">Fixed Rate</option>
                            <option value="incremental">Incremental Rate</option>
                          </select>
                        </div>
                        {formData.gratuity_increment_type === 'incremental' && (
                          <div className="col-xl-6">
                            <label htmlFor="edit_gratuity_start_years" className="form-label">Gratuity Start Years</label>
                            <select
                              className="form-select"
                              id="edit_gratuity_start_years"
                              name="gratuity_start_years"
                              value={formData.gratuity_start_years}
                              onChange={handleInputChange}
                            >
                              <option value="">Select gratuity start years</option>
                              <option value="3">3 Years</option>
                              <option value="4">4 Years</option>
                              <option value="5">5 Years</option>
                            </select>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="modal-footer">
                      <button type="button" className="btn btn-danger" data-bs-dismiss="modal" onClick={handleCloseModal} disabled={submitting}>Cancel</button>
                      <button type="submit" className="btn btn-primary" disabled={submitting}>
                        {submitting ? 'Updating...' : 'Update Company'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
            { /* edit company modal end */}</>
        </div>
        {/* app-content-area-end */}
      </div>
    </div>
  );
};

export default AllCompanies;